import React, { useState, useEffect } from "react";
import {
  Briefcase,
  Plus,
  X,
  Check,
  Edit3,
  EyeOff,
  Eye,
  Trash2,
} from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { useApp } from "../../context/AppContext";
import { directorService } from "../../services/directorService";
import { Card } from "../ui/Card";
import { ActionButton } from "../ui/ActionButton";

export const DirectorsPanel = ({ palette }) => {
  const { showMessage, directors: activeDirectors, setDirectors } = useApp();
  const [allDirectors, setAllDirectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDir, setEditingDir] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formTitle, setFormTitle] = useState("");
  const [saving, setSaving] = useState(false);

  const loadDirectors = async () => {
    try {
      setLoading(true);
      const data = await directorService.getAll();
      setAllDirectors(data);
    } catch (err) {
      showMessage("Failed to load directors", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDirectors(); }, []);

  const resetForm = () => {
    setFormName("");
    setFormEmail("");
    setFormPhone("");
    setFormTitle("");
    setEditingDir(null);
    setShowAdd(false);
  };

  const startEdit = (dir) => {
    setEditingDir(dir);
    setFormName(dir.name);
    setFormEmail(dir.email || "");
    setFormPhone(dir.phone || "");
    setFormTitle(dir.title || "");
    setShowAdd(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) { showMessage("Director name is required", "error"); return; }
    setSaving(true);
    try {
      if (editingDir) {
        await directorService.update(editingDir.id, {
          name: formName.trim(),
          email: formEmail.trim(),
          phone: formPhone.trim(),
          title: formTitle.trim(),
        });
        showMessage("Director updated");
      } else {
        await directorService.create({
          name: formName.trim(),
          email: formEmail.trim(),
          phone: formPhone.trim(),
          title: formTitle.trim(),
          sortOrder: allDirectors.length,
        });
        showMessage("Director added");
      }
      resetForm();
      await loadDirectors();
      const active = await directorService.getActive();
      setDirectors(active);
    } catch (err) {
      showMessage("Failed to save director: " + err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (dir) => {
    try {
      await directorService.update(dir.id, { isActive: !dir.is_active });
      showMessage(dir.is_active ? "Director deactivated" : "Director reactivated");
      await loadDirectors();
      const active = await directorService.getActive();
      setDirectors(active);
    } catch (err) {
      showMessage("Failed to update director", "error");
    }
  };

  const handleDelete = async (dir) => {
    try {
      await directorService.remove(dir.id);
      showMessage("Director removed", "warning");
      await loadDirectors();
      const active = await directorService.getActive();
      setDirectors(active);
    } catch (err) {
      showMessage(err.message || "Failed to delete director", "error");
    }
  };

  const th = {
    padding: "8px 12px",
    textAlign: "left",
    fontSize: "0.65rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: palette.text3,
    borderBottom: `2px solid ${palette.cardBorder}`,
  };

  const td = {
    padding: "7px 12px",
    fontSize: FONT_SIZES.xs,
    fontFamily: FONT_FAMILY,
    borderBottom: `1px solid ${palette.cardBorder}`,
  };

  return (
    <>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Briefcase size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
            <span style={{ fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>Directors</span>
          </div>
          <ActionButton
            label={showAdd ? "Cancel" : "Add Director"}
            icon={showAdd ? X : Plus}
            variant={showAdd ? "secondary" : "primary"}
            small
            onClick={() => { if (showAdd) resetForm(); else setShowAdd(true); }}
          />
        </div>

        {/* Add/Edit form */}
        {showAdd && (
          <Card style={{ marginBottom: 16, padding: 16, background: `${palette.accent1}04` }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", color: palette.text3, fontFamily: FONT_FAMILY, display: "block", marginBottom: 4 }}>
                  Name *
                </label>
                <input
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Full name"
                  style={{
                    padding: "8px 12px", borderRadius: 8, fontSize: FONT_SIZES.sm, fontFamily: FONT_FAMILY,
                    outline: "none", background: palette.inputBg, color: palette.inputColor,
                    border: `1px solid ${palette.inputBorder}`, width: "100%", boxSizing: "border-box",
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", color: palette.text3, fontFamily: FONT_FAMILY, display: "block", marginBottom: 4 }}>
                  Title
                </label>
                <input
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder="e.g. Development Director"
                  style={{
                    padding: "8px 12px", borderRadius: 8, fontSize: FONT_SIZES.sm, fontFamily: FONT_FAMILY,
                    outline: "none", background: palette.inputBg, color: palette.inputColor,
                    border: `1px solid ${palette.inputBorder}`, width: "100%", boxSizing: "border-box",
                  }}
                />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", color: palette.text3, fontFamily: FONT_FAMILY, display: "block", marginBottom: 4 }}>
                  Email
                </label>
                <input
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  placeholder="Email address"
                  type="email"
                  style={{
                    padding: "8px 12px", borderRadius: 8, fontSize: FONT_SIZES.sm, fontFamily: FONT_FAMILY,
                    outline: "none", background: palette.inputBg, color: palette.inputColor,
                    border: `1px solid ${palette.inputBorder}`, width: "100%", boxSizing: "border-box",
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", color: palette.text3, fontFamily: FONT_FAMILY, display: "block", marginBottom: 4 }}>
                  Phone
                </label>
                <input
                  value={formPhone}
                  onChange={(e) => setFormPhone(e.target.value)}
                  placeholder="Phone number"
                  style={{
                    padding: "8px 12px", borderRadius: 8, fontSize: FONT_SIZES.sm, fontFamily: FONT_FAMILY,
                    outline: "none", background: palette.inputBg, color: palette.inputColor,
                    border: `1px solid ${palette.inputBorder}`, width: "100%", boxSizing: "border-box",
                  }}
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <ActionButton label="Cancel" variant="secondary" small onClick={resetForm} />
              <ActionButton
                label={saving ? "Saving..." : (editingDir ? "Update" : "Add")}
                icon={Check}
                small
                onClick={handleSave}
                disabled={saving || !formName.trim()}
              />
            </div>
          </Card>
        )}

        {/* Directors table */}
        {loading ? (
          <div style={{ textAlign: "center", padding: 20, color: palette.text3, fontSize: FONT_SIZES.sm }}>
            Loading directors...
          </div>
        ) : allDirectors.length === 0 ? (
          <div style={{ textAlign: "center", padding: 32, color: palette.text3 }}>
            <Briefcase size={ICON_SIZES.xxl} style={{ marginBottom: 8, opacity: 0.4 }} />
            <p style={{ fontSize: FONT_SIZES.sm, fontFamily: FONT_FAMILY, margin: 0 }}>
              No directors added yet. Add your first director to start assigning donor portfolios.
            </p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT_FAMILY }}>
              <thead>
                <tr>
                  <th style={th}>Name</th>
                  <th style={th}>Title</th>
                  <th style={th}>Email</th>
                  <th style={th}>Phone</th>
                  <th style={th}>Status</th>
                  <th style={{ ...th, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {allDirectors.map((dir) => (
                  <tr key={dir.id} style={{ opacity: dir.is_active ? 1 : 0.5 }}>
                    <td style={{ ...td, fontWeight: 600, color: palette.text1 }}>{dir.name}</td>
                    <td style={{ ...td, color: palette.text2 }}>{dir.title || "—"}</td>
                    <td style={{ ...td, color: palette.text2 }}>{dir.email || "—"}</td>
                    <td style={{ ...td, color: palette.text2 }}>{dir.phone || "—"}</td>
                    <td style={td}>
                      <span style={{
                        padding: "2px 8px", borderRadius: 8, fontSize: "0.65rem", fontWeight: 600,
                        background: dir.is_active ? "#dcfce7" : "#fee2e2",
                        color: dir.is_active ? "#166534" : "#991b1b",
                      }}>
                        {dir.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <ActionButton label="Edit" icon={Edit3} variant="secondary" small onClick={() => startEdit(dir)} />
                        <ActionButton
                          label={dir.is_active ? "Deactivate" : "Activate"}
                          icon={dir.is_active ? EyeOff : Eye}
                          variant="secondary"
                          small
                          onClick={() => handleToggleActive(dir)}
                        />
                        <ActionButton label="Delete" icon={Trash2} variant="danger" small onClick={() => handleDelete(dir)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
};
